/*
 * 🃏 DFCL Starter Decks — read-only reference decks
 * --------------------------------------------------
 * Filed verbatim from Roc. Series-1 legal unless otherwise noted.
 * Do NOT invent cards, change counts, or merge in other-AI brainstorming.
 *
 * Deck validity (see /arcade/rules/dfcl):
 *   • Main Deck: exactly 50
 *   • Finisher Side-Deck: 5–10
 *   • Max 3 copies by title (1 for Divine/God — Series-1 has none)
 *   • At least 1 Fighter, no Fighter cap — 1 designated Starting Vanguard
 *
 * Ownership model (owner directive 2026-10-02 — "only want users to
 * see what they have"):
 *   • `starter: true`       — auto-owned by every player (Welcome)
 *   • `locked: true`        — hidden until won/found/bought
 *   • A user "owns" a deck when its id is in `user.dfcl_decks[]`
 *     (field not yet seeded; the UI falls back to `starter` only).
 */

export const WELCOME_DECK = {
  id:       "welcome-to-dragonious",
  name:     "Welcome to Dragonious Deck",
  order:    10,
  by:       "Roc",
  dated:    "2026-09-17",
  starter:  true,  // ✅ auto-owned by every player
  strategy: "The welcome tour — a little of everything the universe does: fusion, healing, tricks, walls, and heart. The first deck a new player should hold.",
  legality: "Fully Series-1 legal (no Gods, no Fighters content).",
  // Pre-verified math by Roc.
  math: {
    main_deck: { current: 50, cap: 50 },
    finisher:  { current: 5, min: 5, max: 10 },
    fighters:  { current: 8, min: 1 },
    copy_limit_ok: true,
  },
  main: [
    {
      section: "Fighters",
      count:   8,
      tone:    "emerald",
      cards: [
        { name: "Bram, Guardian of the Forest",            qty: 2, note: "mascot — Roc's favorite" },
        { name: "Xyla, The Flesh Harvester",               qty: 1 },
        { name: "David Lauer, Grand Vanguard & Tactician", qty: 1 },
        { name: "Mother's Phoenix",                        qty: 1 },
        { name: "Glitch, The Unbound Anomaly",             qty: 1 },
        { name: "Chief Wesley Blanks",                     qty: 1 },
        { name: "Miko, the Tinkerer",                      qty: 1 },
      ],
    },
    {
      section: "Units",
      count:   10,
      tone:    "zinc",
      cards: [
        { name: "Runt, Ashen Scavenger", qty: 3 },
        { name: "Flame Mercenary",       qty: 2 },
        { name: "Phoenix Acolyte",       qty: 2 },
        { name: "Ashen Monk",            qty: 2 },
        { name: "Iron Brawler",          qty: 1 },
      ],
    },
    {
      section: "Spells",
      count:   20,
      tone:    "slate",
      cards: [
        { name: "The Bro Code",             qty: 2 },
        { name: "Dash Glitch",              qty: 2 },
        { name: "Dragonious Bending",       qty: 2 },
        { name: "Embersweet Fruit",         qty: 3 },
        { name: "Flame Synthesis",          qty: 2, note: "taste of fusion" },
        { name: "Mother's Phoenix Feather", qty: 2 },
        { name: "Momentum",                 qty: 2 },
        { name: "Glitch (spell)",           qty: 1 },
        { name: "Flash Ignition",           qty: 1 },
        { name: "Weekly Quest",             qty: 1, note: "meet Taylor" },
        { name: "Deanah's Diary",           qty: 1, note: "where the story starts" },
        { name: "One More Rush",            qty: 1, note: "the spirits say hi" },
      ],
    },
    {
      section: "Traps",
      count:   6,
      tone:    "red",
      cards: [
        { name: "Stone Defense",                   qty: 2 },
        { name: `"Cut It Out or I'll Cut You"`,    qty: 2 },
        { name: "Temporal Retaliation",            qty: 2 },
      ],
    },
    {
      section: "Fields",
      count:   6,
      tone:    "indigo",
      cards: [
        { name: "Dave's Ashen Diner",     qty: 2 },
        { name: "Bram's Forest of Peace", qty: 1 },
        { name: "Aethelgard Bazaar",      qty: 1 },
        { name: "Aqua Aquifer",           qty: 1 },
        { name: "Mirage River",           qty: 1 },
      ],
    },
  ],
  finisher: {
    count: 5,
    tone:  "amber",
    cards: [
      { name: "Mother's Phoenix",             qty: 1 },
      { name: "Blazing Sovereign Phoenix",    qty: 1 },
      { name: "Magma Juggernaut",             qty: 1 },
      { name: "Combustion Champion",          qty: 1 },
      { name: "Bram, Guardian of the Sanctuary", qty: 1 },
    ],
  },
  engine_lines: [
    { title: "Meet the family",     body: "one of almost everyone — Bram, Xyla, David, Mother Phoenix, Glitch, Wesley, Miko." },
    { title: "Taste of everything", body: "a fusion card, a heal, a trick, a wall, a quest, the diary where it all started." },
    { title: "The lesson",          body: "every card is a doorway. New players learn the universe by playing it." },
  ],
};

// Export list for the Starter Deck selector — one entry for now; grows
// as Roc files more decks. Order follows Roc's deck numbering.
// Declared BELOW the deck constants to avoid TDZ (consts aren't hoisted).

// 🥊 Cari — Hero Deck (filed 2026-09-18, updated 2026-09-18 with #185–196).
// Hero-themed build. LOCKED by default — players win / find / buy it.
// Deck math (verified): 9 hero + 33 staples + 8 backfill = 50 ✓
// Fighters in deck: 2 (no Fighter cap). Some Fighters-body versions marked [S1✗]
// are NOT Series-1 legal.
export const CARI_HERO_DECK = {
  id:       "cari-hero-deck",
  name:     "Cari — Hero Deck",
  order:    76,  // Cari's hero number
  by:       "Roc",
  dated:    "2026-09-18",
  locked:   true,  // 🔒 hidden until won/found/bought
  unlock_hint: "Win her side of the story, trade for her tome, or buy her feather.",
  strategy: "Hero deck built around Cari, The War Librarian (#76). 2026-09-18 refresh added the #185–196 staples; ⟳ backfill slots swap out freely as new Cari cards land.",
  legality: "Hero deck · 2 Fighters in deck (no Fighter cap). Series-1 restricted: some Fighters-body versions marked [S1✗].",
  math: {
    main_deck: { current: 50, cap: 50 },
    finisher:  { current: 0, min: 5, max: 10 },   // Finisher Zone not provided in this build
    fighters:  { current: 2, min: 1 },
    copy_limit_ok: true,
  },
  main: [
    {
      section: "Hero Cards (their own)",
      count:   9,
      tone:    "emerald",
      cards: [
        { name: "#77 General Cari (Fighters) 🥊",   qty: 3, note: "S1✗ — Fighters-body version" },
        { name: "#76 Cari, The War Librarian 🥊",   qty: 3 },
        { name: "#91 Kneel Before the General",     qty: 3, note: "S1✗ — Fighters-body version" },
      ],
    },
    {
      section: "New Staples (#185–196)",
      count:   33,
      tone:    "slate",
      cards: [
        { name: "#185 Waterlogged",             qty: 3 },
        { name: "#186 Soul Exchange",           qty: 3 },
        { name: "#187 Soul Eater 1",            qty: 3 },
        { name: "#188 Soul Eater 2",            qty: 3 },
        { name: "#189 Soul Eater 3",            qty: 3 },
        { name: "#190 Soul Eater 4",            qty: 3 },
        { name: "#191 Soul Eater",              qty: 3 },
        { name: "#192 The Oasis",               qty: 3 },
        { name: "#194 Battle Bag",              qty: 3 },
        { name: "#195 Astral Mist",             qty: 3 },
        { name: "#196 The Anomalous Tube TV",   qty: 3 },
      ],
    },
    {
      section: "Backfill ⟳ (neutral staples)",
      count:   8,
      tone:    "zinc",
      cards: [
        { name: "#124 Inspection ⟳",                  qty: 3, note: "neutral — swap when new Cari drops" },
        { name: "#130 Information Bomb ⟳",            qty: 3, note: "neutral — swap when new Cari drops" },
        { name: "#135 Eye Spy (With My Little Eye) ⟳", qty: 2, note: "neutral — swap when new Cari drops" },
      ],
    },
  ],
  finisher: {
    count: 0,
    tone:  "amber",
    // Roc has not filed Cari's Finisher Zone yet — placeholder notes
    // the slot stays open (5–10 legal when he drops them).
    cards: [],
    pending_note: "Finisher Zone pending — Roc hasn't filed Cari's Showtimes/Fatalities yet.",
  },
  engine_lines: [
    { title: "Hero spine",       body: "Cari + General Cari + Kneel Before the General — the War Librarian's own stack." },
    { title: "Soul Eater line",  body: "five Soul Eater variants (base + 1–4) in triplicate run the soul-exchange engine." },
    { title: "Backfill rotation", body: "⟳ cards are placeholders — new Cari drops replace them freely, no permission needed." },
  ],
};

// 🪣 Nathaniel Bucket — Hero Deck (filed 2026-09-18, updated 2026-09-18
// with #185–196). Hero-themed build. LOCKED by default — players win /
// find / buy it. Deck math: 9 hero + 33 staples + 8 backfill = 50 ✓
// Fighters in deck: 2 (no Fighter cap). Some Fighters-body versions marked [S1✗]
// are NOT Series-1 legal.
export const NATHANIEL_BUCKET_HERO_DECK = {
  id:       "nathaniel-bucket-hero-deck",
  name:     "Nathaniel Bucket — Hero Deck",
  order:    69,  // Nathaniel Bucket's hero number
  by:       "Roc",
  dated:    "2026-09-18",
  locked:   true,  // 🔒 hidden until won/found/bought
  unlock_hint: "Win his side of the story, trade for his bucket, or buy his feather.",
  strategy: "Hero deck built around Nathaniel Bucket (#69). 2026-09-18 refresh added the #185–196 staples; ⟳ backfill slots swap out freely as new Nathaniel cards land.",
  legality: "Hero deck · 2 Fighters in deck (no Fighter cap). Series-1 restricted: some Fighters-body versions marked [S1✗].",
  math: {
    main_deck: { current: 50, cap: 50 },
    finisher:  { current: 0, min: 5, max: 10 },   // Finisher Zone not filed yet
    fighters:  { current: 2, min: 1 },
    copy_limit_ok: true,
  },
  main: [
    {
      section: "Hero Cards (their own)",
      count:   9,
      tone:    "emerald",
      cards: [
        { name: "#69 Nathaniel Bucket 🥊",            qty: 3 },
        { name: "#96 Nathaniel Bucket (Fighters) 🥊", qty: 3, note: "S1✗ — Fighters-body version" },
        { name: "#141 Scrappy",                        qty: 3 },
      ],
    },
    {
      section: "New Staples (#185–196)",
      count:   33,
      tone:    "slate",
      cards: [
        { name: "#185 Waterlogged",             qty: 3 },
        { name: "#186 Soul Exchange",           qty: 3 },
        { name: "#187 Soul Eater 1",            qty: 3 },
        { name: "#188 Soul Eater 2",            qty: 3 },
        { name: "#189 Soul Eater 3",            qty: 3 },
        { name: "#190 Soul Eater 4",            qty: 3 },
        { name: "#191 Soul Eater",              qty: 3 },
        { name: "#192 The Oasis",               qty: 3 },
        { name: "#194 Battle Bag",              qty: 3 },
        { name: "#195 Astral Mist",             qty: 3 },
        { name: "#196 The Anomalous Tube TV",   qty: 3 },
      ],
    },
    {
      section: "Backfill ⟳ (neutral staples)",
      count:   8,
      tone:    "zinc",
      cards: [
        { name: "#21 The Blackstone Brew ⟳", qty: 3, note: "neutral — swap when new Nathaniel drops" },
        { name: "#34 Momentum ⟳",            qty: 3, note: "neutral — swap when new Nathaniel drops" },
        { name: "#116 One More Rush ⟳",      qty: 2, note: "neutral — swap when new Nathaniel drops" },
      ],
    },
  ],
  finisher: {
    count: 0,
    tone:  "amber",
    cards: [],
    pending_note: "Finisher Zone pending — Roc hasn't filed Nathaniel's Showtimes/Fatalities yet.",
  },
  engine_lines: [
    { title: "Hero spine",        body: "Nathaniel + the Fighters-body + Scrappy — the Commoner Vanguard's own stack." },
    { title: "Soul Eater line",   body: "five Soul Eater variants (base + 1–4) in triplicate run the soul-exchange engine." },
    { title: "Backfill rotation", body: "⟳ cards are placeholders — new Nathaniel drops replace them freely, no permission needed." },
  ],
};

// 📚 Full catalog, in Roc's deck-number order. Ownership gating in the
// UI filters this list down to only decks the player owns + starter decks.
export const DFCL_STARTER_DECKS = [WELCOME_DECK, NATHANIEL_BUCKET_HERO_DECK, CARI_HERO_DECK];

/**
 * 🔑 Deck ownership check. A deck is "owned" when:
 *   • it's a starter (auto-granted to every player), OR
 *   • its id is listed in `user.dfcl_decks[]` (future gameplay / shop grant)
 * Owner always sees every deck for QA (role === "owner").
 */
export function ownsDeck(deck, user) {
  if (!deck) return false;
  if (deck.starter) return true;
  if (!user) return false;
  if (user.role === "owner") return true;
  const owned = (user.dfcl_decks || []);
  return owned.includes(deck.id);
}

/** Convenience — list of decks the user can see right now. */
export function visibleDecks(user) {
  return DFCL_STARTER_DECKS.filter((d) => ownsDeck(d, user));
}

/** Count of locked decks (for the "N decks locked" teaser footer). */
export function lockedDeckCount(user) {
  return DFCL_STARTER_DECKS.length - visibleDecks(user).length;
}

